import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import apis from '@src/apis';
import JSZip from 'jszip';
import { getFileName } from '@src/utils/file';
import { removeVietnameseTones } from '@src/utils/string';
import {
  DEFAULT_BREAK_TIME,
  DEFAULT_SPEED,
  MERGED_BREAK_TIME_REGEX,
  VOICE_PROVIDER,
} from '@src/constants/voice';
import {
  VALID_CHARACTERS_LENGTH_REGEX,
  VALID_CHARACTERS_REGEX,
} from '@src/constants/tts';
import { containsBlacklistedWords } from '@src/services/blackword';
import axios from 'axios';
import { saveAs } from 'file-saver';
import {
  BLOCK_STATUS,
  CONVERTING_STATUS,
  DIGIT_SRT_TIME_REGEX,
  DUBBING_MAX_DURATION,
  SRT_TIME_REGEX,
  TITLE_FILE_REGEX,
  TRANSLATE_CREDIT_PER_CHAR,
  PROJECT_TYPE,
  PROJECT_TYPE_KIND,
  EXPORT_STATUS,
  EXPORT_HISTORY_TYPES,
  EXPORT_RETENTION_PERIOD,
  EXTRACTION_STATUS,
  PROJECT_PRODUCT,
  REWRITE_STATUS,
} from '@src/constants/project';
import {
  PRESENTATION_FILE_FORMAT,
  EXPORT_TYPE,
} from '@src/constants/presentation';
import {
  ASPECT_RATIO_VALUES,
  CAPTION_STYLE_CUSTOM,
  CAPTION_STYLE_NONE,
  DEFAULT_VIDEO_BACKGROUND_COLOR,
  DUB_FILE_FORMAT,
  MAX_CANVAS_DIMENSION,
} from '@src/constants/dubbing';
import { TRANSCRIPT_FILE_FORMAT } from '@src/constants/transcript';
import { CAPTION_STYLE_DEFAULTS } from '@vbee-holding/aivoice-shared-lib';
import NormalProjectIcon from '@src/assets/icons/project-normal.svg';
import PresentationProjectIcon from '@src/assets/icons/project-presentation.svg';
import SrtProjectIcon from '@src/assets/icons/project-srt.svg';
import { RESOURCE } from '@src/constants';
import { API_URL } from '@src/configs';
import {
  convertSecondsToSubtitleTime,
  convertSubtitleTimeToSeconds,
} from '@src/utils/time';
import { countTextLength } from './tts';
import { getEndBlockDurationSec } from './timeline';

// Icon mapping — mirrors legacy TitleProject + FileDropzoneV3:
// - PresentationProjectIcon: PPT/PPTX (product PRESENTATION)
// - SrtProjectIcon: SRT + audio/video (product DUBBING)
// - NormalProjectIcon: text files txt/doc/docx/pdf (product TTS)
// `.ppt` is added beyond PRESENTATION_FILE_FORMAT (['pptx']) so legacy `.ppt`
// files in the list still render the presentation icon — does not affect
// upload validation (dropzone still only accepts PRESENTATION_FILE_FORMAT).
const PRESENTATION_ICON_EXTENSIONS = [...PRESENTATION_FILE_FORMAT, 'ppt'];
const DUBBING_ICON_EXTENSIONS = [...DUB_FILE_FORMAT, ...TRANSCRIPT_FILE_FORMAT];

/**
 * Resolve the icon asset for a given file based on its extension.
 *
 * @param {File | { name: string }} file
 * @returns {string} Icon src (imported SVG asset URL).
 */
const getFileIcon = (file) => {
  const ext = file?.name?.split('.').pop()?.toLowerCase() || '';
  if (!ext) return NormalProjectIcon;
  if (PRESENTATION_ICON_EXTENSIONS.includes(ext))
    return PresentationProjectIcon;
  if (DUBBING_ICON_EXTENSIONS.includes(ext)) return SrtProjectIcon;
  return NormalProjectIcon;
};

const sanitizeTitleFile = (text) =>
  text
    .trim()
    .replace(MERGED_BREAK_TIME_REGEX, '')
    .replace(TITLE_FILE_REGEX, '')
    .substring(0, 30);

const handleDownloadParagraphsZip = async (
  audioLinks,
  title,
  audioType,
  onProgress,
) => {
  const zip = new JSZip();
  const nameFile = `Vbee_${title}.zip`;
  const folder = zip.folder(`Vbee_${title}`);

  const BATCH_SIZE = 100;
  const DELAY_BETWEEN_BATCHES = 25;
  const TIMEOUT = 12000;

  let completed = 0;
  let failed = 0;

  const updateProgress = () => {
    if (onProgress) {
      const percentage = Math.round((completed / audioLinks.length) * 100);
      onProgress({ completed, failed, total: audioLinks.length, percentage });
    }
  };

  const downloadFile = async (url, index) => {
    try {
      const res = await apis.requests.getAudioDownloadUrl(url.requestId);
      const audioLink = res.result?.audio;
      const audioName = getFileName(audioLink);

      if (!audioLink) throw new Error('No audio link');

      const response = await axios.get(audioLink, {
        responseType: 'blob',
        timeout: TIMEOUT,
      });

      const order = url?.position ? url.position + 1 : index + 1;
      const filename = `${order}_${audioName}.${audioType}`;

      folder.file(filename, response.data);
      completed += 1;
      updateProgress();

      return true;
    } catch (error) {
      failed += 1;
      updateProgress();
      return false;
    }
  };

  for (let i = 0; i < audioLinks.length; i += BATCH_SIZE) {
    const batch = audioLinks.slice(i, i + BATCH_SIZE);

    // eslint-disable-next-line no-await-in-loop
    await Promise.allSettled(
      batch.map((url, idx) => downloadFile(url, i + idx)),
    );

    if (i + BATCH_SIZE < audioLinks.length) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) =>
        setTimeout(resolve, DELAY_BETWEEN_BATCHES),
      );
    }
  }

  const zipBlob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 3 },
    streamFiles: true,
  });

  saveAs(zipBlob, nameFile);
  return { completed, failed, total: audioLinks.length };
};

const convertBlocksSaveToStore = (blocks) => {
  // Ensure each block has an ID
  const blocksWithIds = blocks.map((block) => ({
    ...block,
    text: block.text || '',
    id: block.id || uuidv4(),
  }));

  return {
    blockIds: blocksWithIds.map((block) => block.id),
    originalBlockIds: blocksWithIds.map((block) => block.id),
    blocks: blocksWithIds.reduce((acc, block) => {
      acc[block.id] = block;
      return acc;
    }, {}),
    originalBlocks: blocksWithIds.reduce((acc, block) => {
      acc[block.id] = block;
      return acc;
    }, {}),
  };
};

const assignEmphasisToBlocks = (blocks) =>
  blocks.map((block) => ({
    ...block,
    emphasis: block.emphasisIntensity || 0,
    hasEmphasis: block.voice?.hasEmphasis || false,
  }));

const countTextLengthBlocks = (blocks, textField = 'text') => {
  const totalLength = Object.keys(blocks).reduce(
    (acc, blockId) => acc + countTextLength(blocks[blockId]?.[textField] || ''),
    0,
  );
  return totalLength;
};

const isBlockChanged = (block, originalBlock) => {
  const newBlocks = JSON.stringify(block);
  const newOriginalBlocks = JSON.stringify(originalBlock);

  if (newBlocks !== newOriginalBlocks) {
    return true;
  }
  return false;
};

const normalizeNote = (note) => (note || '').trim();

const getBlockMap = (blocks) =>
  Object.keys(blocks)
    .map((blockId) => ({
      id: blockId,
      title: blocks[blockId].title,
      text: blocks[blockId].text,
      voiceCode: blocks[blockId].voiceCode,
      speed: blocks[blockId].speed,
      emphasis: blocks[blockId].emphasis,
      ...(blocks[blockId].translatedText != null && {
        translatedText: blocks[blockId].translatedText,
      }),
    }))
    .reduce((acc, block) => {
      acc[block.id] = block;
      return acc;
    }, {});

// The note is unrelated to a block's synthesis status, so it is NOT part of
// getBlockMap (block-content compare). It is compared separately here so an
// edited note still marks the project dirty (unsaved-changes prompt on back)
// without flipping a synthesized block back to draft.
const haveNotesChanged = (blocks, originalBlocks) =>
  Object.keys(blocks).some(
    (blockId) =>
      normalizeNote(blocks[blockId]?.currentNote) !==
      normalizeNote(originalBlocks[blockId]?.currentNote),
  );

// `includeNotes` lets callers ignore note changes — e.g. a video export does not
// render notes, so a note-only edit must not mark it changed; pptx keeps notes.
const checkBlocksChanged = ({
  blocks,
  originalBlocks,
  blockIds,
  originalBlockIds,
  includeNotes = true,
}) => {
  if (!blocks || !originalBlocks) return false;
  const newBlockIds = JSON.stringify(blockIds);
  const newOriginalBlockIds = JSON.stringify(originalBlockIds);
  if (newBlockIds !== newOriginalBlockIds) return true;
  const blockMap = getBlockMap(blocks);
  const originalBlockMap = getBlockMap(originalBlocks);
  if (isBlockChanged(blockMap, originalBlockMap)) return true;
  return includeNotes ? haveNotesChanged(blocks, originalBlocks) : false;
};

const audioExpired = (time) => {
  if (!time) return false;
  return moment().isAfter(time);
};

const getBlockChanged = (blockWithText, originalBlocks) =>
  blockWithText.filter((block) => {
    const currentBlock = {
      title: block?.title,
      text: block?.text,
      voiceCode: block?.voiceCode,
      speed: block?.speed,
      emphasis: block?.emphasis,
    };

    const originalBlock = {
      title: originalBlocks[block.id]?.title,
      text: originalBlocks[block.id]?.text,
      voiceCode: originalBlocks[block.id]?.voiceCode,
      speed: originalBlocks[block.id]?.speed,
      emphasis: originalBlocks[block.id]?.emphasis,
    };

    return isBlockChanged(currentBlock, originalBlock) || !block?.audioLink;
  });

// After a successful translation the spoken text is the translation, so the
// field an edit lands in is translatedText — the same rule getBlockStatus
// applies. A block created after the translation has its whole content there
// and nothing in `text`, so whatever prices or gates that block's synthesis
// reads this too, or it reads an empty string off a block full of text. A
// caller that leaves isTranslationSuccess off keeps reading the source text.
const getEffectiveText = (block, isTranslationSuccess) =>
  (isTranslationSuccess ? block?.translatedText : block?.text) || '';

const getDubbingBlockChanged = (
  blockWithText,
  originalBlocks,
  isTranslationSuccess = false,
) =>
  blockWithText.filter((block) => {
    const currentBlock = {
      text: getEffectiveText(block, isTranslationSuccess),
      voiceCode: block?.voiceCode,
      speed: block?.speed,
      startTime: block?.startTime,
      endTime: block?.endTime,
      speakerId: block?.speakerId,
    };
    const originalBlock = {
      text: getEffectiveText(originalBlocks[block.id], isTranslationSuccess),
      voiceCode: originalBlocks[block.id]?.voiceCode,
      speed: originalBlocks[block.id]?.speed,
      startTime: originalBlocks[block.id]?.startTime,
      endTime: originalBlocks[block.id]?.endTime,
      speakerId: originalBlocks[block.id]?.speakerId,
    };
    return isBlockChanged(currentBlock, originalBlock);
  });

// Video dubbing audio download: block timing (startTime/endTime) does not
// change the synthesized audio itself, and endTime auto-shrinks after synthesis
// (getAudioAdjustedBlockEndTime), so timing must NOT count as a user change
// here — unlike getDubbingBlockChanged, which keeps timing for SRT accuracy.
const getVideoDubbingBlockChanged = (
  blockWithText,
  originalBlocks,
  isTranslationSuccess = false,
) =>
  blockWithText.filter((block) => {
    const currentBlock = {
      text: getEffectiveText(block, isTranslationSuccess),
      speed: block?.speed,
      speakerId: block?.speakerId,
    };
    const originalBlock = {
      text: getEffectiveText(originalBlocks[block.id], isTranslationSuccess),
      speed: originalBlocks[block.id]?.speed,
      speakerId: originalBlocks[block.id]?.speakerId,
    };
    return isBlockChanged(currentBlock, originalBlock);
  });

/**
 * Remove audioExpiredAt from blocks to reset expired status
 * @param {Array} blockIds - Array of block IDs
 * @param {Object} blocks - Object containing all blocks
 * @returns {Array} - Array of blocks with Audio link
 */
const getBlockWithAudio = (blockIds, blocks) =>
  blockIds
    .filter((id) => blocks[id]?.audioLink)
    .map((id) => {
      const block = blocks[id];
      if (block.audioExpiredAt) {
        return {
          ...block,
          audioExpiredAt: null,
        };
      }
      return block;
    });

const checkBlockTextError = (
  text,
  maxCharLimit,
  blackWordsList = [],
  voice = null,
  options = {},
) => {
  const { skipInvalidCharacters = false } = options;
  const exceedMaxChar = countTextLength(text) > maxCharLimit;

  const isVbeeVoice =
    (voice && !voice?.provider) || voice?.provider === VOICE_PROVIDER.VBEE;

  const invalidCharacterLengthTexts = isVbeeVoice
    ? text?.match(VALID_CHARACTERS_LENGTH_REGEX)
    : null;
  const hasInvalidCharacterLength = !!invalidCharacterLengthTexts?.length;

  const hasInvalidCharacters =
    !skipInvalidCharacters &&
    isVbeeVoice &&
    !!text?.length &&
    !VALID_CHARACTERS_REGEX.test(text);

  const blackWordsFound = blackWordsList.filter((blackWord) =>
    containsBlacklistedWords({ text, blackWord }),
  );
  const hasBlackWords = blackWordsFound.length > 0;

  const hasError =
    exceedMaxChar ||
    hasInvalidCharacterLength ||
    hasInvalidCharacters ||
    hasBlackWords;

  return {
    exceedMaxChar,
    invalidCharacterLengthTexts,
    hasInvalidCharacterLength,
    hasInvalidCharacters,
    hasBlackWords,
    blackWordsFound,
    hasError,
  };
};

// The voice a block is actually read with — and the one every text check has to
// run against. `checkBlockTextError` only flags characters the voice's language
// cannot speak when it can see a Vbee voice, so resolving the voice differently
// per call site makes the same block come out ERROR in one place and DRAFT in
// another: the block list shows the error icon while the status filter, working
// off another voice, does not count it as an error at all.
//
// A block can carry its own voice (projects from before speakers existed),
// otherwise it reads with the voice of the speaker it is assigned to, and
// `voiceDefault` covers a project with no speakers.
const resolveBlockVoice = ({ block, speakers, voiceDefault }) => {
  if (block?.voice) return block.voice;
  const speaker = speakers?.find((item) => item.id === block?.speakerId);
  return speaker?.voice || voiceDefault || null;
};

const getBlockStatus = ({
  block,
  originalBlock,
  audioType,
  maxCharLimit,
  blackWordsList = [],
  voice = null,
  isTranslationSuccess = false,
}) => {
  // After a successful translation the spoken text is the translation, so both
  // the error check and the change detection below must run on translatedText,
  // not the original text.
  const effectiveText = isTranslationSuccess
    ? block?.translatedText || ''
    : block?.text || '';
  const effectiveOriginalText = isTranslationSuccess
    ? originalBlock?.translatedText || ''
    : originalBlock?.text || '';
  const { hasError } = checkBlockTextError(
    effectiveText,
    maxCharLimit,
    blackWordsList,
    voice,
  );
  const isError = block?.status === CONVERTING_STATUS.FAILURE || hasError;
  if (isError) return BLOCK_STATUS.ERROR;

  // Check if block is in progress first. Because:
  // 1. When audio type change, only the currentBlock change, the originalBlock is not change
  // 2. When synthesize a block or project, the content of originalBlock will be updated but not the audio link
  // That will make the currentBlock and originalBlock always different, which will make DRAFT condition always true
  // 3. When that happen, the code will not be able to reach to IN_PROGRESS condition
  const isInProgress = block?.status === CONVERTING_STATUS.IN_PROGRESS;
  if (isInProgress) return BLOCK_STATUS.IN_PROGRESS;

  // Check if the block is changed
  const currentBlockAudioType = block?.audioLink?.split('.')?.pop();
  const currentBlock = {
    text: effectiveText,
    voiceCode: block?.voiceCode,
    speed: block?.speed,
    audioType: currentBlockAudioType || audioType,
    emphasis: block?.emphasis,
  };
  const currentOriginalBlock = {
    text: effectiveOriginalText,
    voiceCode: originalBlock?.voiceCode,
    speed: originalBlock?.speed,
    audioType,
    emphasis: originalBlock?.emphasis,
  };
  // Originally, we would compare audioType with originalAudioType
  // But now, we would compare audioType with currentBlockAudioType
  // Because when audio type change, audioType will reflect the newest audio type
  // But originalAudioType will not reflect the newest audio type
  const isDraft =
    isBlockChanged(currentBlock, currentOriginalBlock) ||
    audioType !== currentBlockAudioType;
  if (isDraft) return BLOCK_STATUS.DRAFT;

  const isSuccess =
    block?.status === CONVERTING_STATUS.SUCCESS &&
    block.audioLink !== null &&
    (block.audioExpiredAt ? moment().isBefore(block.audioExpiredAt) : true);
  // Make sure the audioExpiredAt is available
  // The check is to avoid the case where success synthesis but the audioExpiredAt is null
  // The reason the audioExpiredAt is null when success synthesis is to remove the audio expired icon
  if (isSuccess) return BLOCK_STATUS.AUDIO_AVAILABLE;

  const isAudioExpired =
    block.audioLink !== null &&
    block?.audioExpiredAt &&
    moment().isAfter(block.audioExpiredAt);
  if (isAudioExpired) return BLOCK_STATUS.AUDIO_EXPIRED;

  return BLOCK_STATUS.DRAFT;
};

const getVoiceFromBlockSelected = ({
  selectedBlockIds,
  blocks,
  voiceDefault,
}) => {
  if (!selectedBlockIds.length) return null;

  const voices = selectedBlockIds
    .map((blockId) => blocks[blockId]?.voice || voiceDefault)
    .filter((voice) => voice?.code);

  if (!voices.length) return null;

  const firstVoice = voices[0];
  const allSameVoiceCode = voices.every(
    (voice) => voice?.code === firstVoice?.code,
  );

  return allSameVoiceCode ? firstVoice : null;
};

const getSpeakerFromBlockSelected = ({
  selectedBlockIds,
  blocks,
  speakers = [],
}) => {
  if (!selectedBlockIds.length) return null;

  const speakerIds = selectedBlockIds
    .map((blockId) => blocks[blockId]?.speakerId)
    .filter((speakerId) => speakerId);

  if (!speakerIds.length) return null;

  const firstSpeakerId = speakerIds[0];
  const allSameSpeakerId = speakerIds.every(
    (speakerId) => speakerId === firstSpeakerId,
  );

  if (!allSameSpeakerId) return null;

  return speakers.find((speaker) => speaker?.id === firstSpeakerId) || null;
};

const getSpeedFromBlockSelected = (selectedBlockIds, blocks) => {
  if (!selectedBlockIds.length) return null;
  const speeds = selectedBlockIds
    .map((blockId) => blocks[blockId]?.speed)
    .filter((speed) => speed);
  if (!speeds.length) return null;
  const allSameSpeed = speeds.every((speed) => speed === speeds[0]);
  return allSameSpeed ? speeds[0] : null;
};

const getEmphasisFromBlockSelected = (selectedBlockIds, blocks) => {
  if (!selectedBlockIds.length) return null;
  const emphases = selectedBlockIds.map((blockId) => blocks[blockId]?.emphasis);
  if (!emphases.length) return null;
  const allSameEmphasis = emphases.every(
    (emphasis) => emphasis === emphases[0],
  );
  return allSameEmphasis ? emphases[0] : null;
};

/**
 * Realign a dubbing block's play slot to its freshly-synthesized audio, per
 * `block duration = MIN(block duration, audio duration)`.
 * - Audio LONGER than the block slot: keep endTime unchanged — the extra audio
 *   is trimmed at the end during playback (getTrimmedAudioDuration).
 * - Audio SHORTER than the block slot: shrink endTime to `startTime +
 *   audioDuration` (the timeline can't hold an object longer than its content).
 * Only ever shrinks the slot; never extends it.
 *
 * @param {Object} params
 * @param {string} params.startTime - Block startTime (HH:mm:ss,SSS)
 * @param {string} params.endTime - Block current endTime (HH:mm:ss,SSS)
 * @param {number} params.audioDuration - Synthesized audio length in seconds
 * @returns {string | null} New endTime, or null when unchanged / not applicable
 */
const getAudioAdjustedBlockEndTime = ({
  startTime,
  endTime,
  audioDuration,
}) => {
  if (!audioDuration || audioDuration <= 0) return null;
  const startSeconds = startTime ? convertSubtitleTimeToSeconds(startTime) : 0;
  const currentEndSeconds = endTime ? convertSubtitleTimeToSeconds(endTime) : 0;
  // MIN(block duration, audio duration): only shrink when the audio is shorter
  // than the current slot; longer audio keeps the slot and is trimmed on play.
  const newEndSeconds = Math.min(
    currentEndSeconds,
    startSeconds + audioDuration,
  );
  // Skip degenerate slots and no-op updates (endTime already correct / longer).
  if (newEndSeconds <= startSeconds) return null;
  if (Math.abs(newEndSeconds - currentEndSeconds) < 0.001) return null;
  return convertSecondsToSubtitleTime(Number(newEndSeconds.toFixed(3)));
};

/**
 * Validate block time change for dubbing projects
 * @param {Object} params
 * @param {'start' | 'end'} params.type - Type of time being changed
 * @param {string} params.newTime - New time value (HH:mm:ss,SSS)
 * @param {Object} params.currentBlock - Current block with startTime/endTime
 * @param {Object} params.prevBlock - Previous block (nullable)
 * @param {Object} params.nextBlock - Next block (nullable)
 * @returns {{ valid: boolean, errorKey: string | null }}
 */
const validateBlockTimeChange = ({
  type,
  newTime,
  currentBlock,
  prevBlock,
  nextBlock,
}) => {
  if (!newTime || !currentBlock) {
    return { valid: false, errorKey: 'invalidTimeFormat' };
  }

  // Validate SRT time format: HH:MM:SS,mmm (e.g., "00:00:01,420")
  if (!SRT_TIME_REGEX.test(newTime)) {
    return { valid: false, errorKey: 'invalidTimeFormat' };
  }

  const newTimeSeconds = convertSubtitleTimeToSeconds(newTime);
  const startTimeSeconds = currentBlock.startTime
    ? convertSubtitleTimeToSeconds(currentBlock.startTime)
    : 0;
  const endTimeSeconds = currentBlock.endTime
    ? convertSubtitleTimeToSeconds(currentBlock.endTime)
    : 0;

  if (type === 'end') {
    // End time must be greater than start time of current block
    if (newTimeSeconds <= startTimeSeconds) {
      return { valid: false, errorKey: 'endTimeMustBeGreaterThanStartTime' };
    }
    // End time must be less than start time of next block
    if (nextBlock?.startTime) {
      const nextStartSeconds = convertSubtitleTimeToSeconds(
        nextBlock.startTime,
      );
      if (newTimeSeconds > nextStartSeconds) {
        return {
          valid: false,
          errorKey: 'endTimeMustBeLessThanOrEqualNextBlockStartTime',
        };
      }
    }
    if (newTimeSeconds > DUBBING_MAX_DURATION) {
      return { valid: false, errorKey: 'endTimeMustBeLessThanOrEqual4Hours' };
    }
  }

  if (type === 'start') {
    // Start time must be less than end time of current block
    if (newTimeSeconds >= endTimeSeconds) {
      return { valid: false, errorKey: 'startTimeMustBeLessThanEndTime' };
    }
    // Start time must be greater than end time of previous block
    if (prevBlock?.endTime) {
      const prevEndSeconds = convertSubtitleTimeToSeconds(prevBlock.endTime);
      if (newTimeSeconds < prevEndSeconds) {
        return {
          valid: false,
          errorKey: 'startTimeMustBeGreaterOrEqualThanPrevBlockEndTime',
        };
      }
    }
  }

  return { valid: true, errorKey: null };
};

/**
 * Calculate timing data for inserting a new block between two blocks (for dubbing projects)
 * @param {Object} currentBlock - The current block with endTime
 * @param {Object} nextBlock - The next block with startTime (optional)
 * @param {number} blockMargin - Margin time in seconds
 * @returns {Object} - Object with startTime and endTime for the new block
 */
const buildInsertedBlockTiming = ({
  currentBlock,
  nextBlock,
  blockMargin,
  maxDurationSec = Infinity,
}) => {
  if (!currentBlock?.endTime) {
    // Empty project's first block → default duration. A leading-gap block
    // (there's a next one) fills [0, next start) like a between-clips insert.
    if (!nextBlock?.startTime) {
      return {
        startTime: convertSecondsToSubtitleTime(0),
        endTime: convertSecondsToSubtitleTime(
          getEndBlockDurationSec(0, maxDurationSec),
        ),
      };
    }
    const nextStartSec = convertSubtitleTimeToSeconds(nextBlock.startTime);
    return {
      startTime: convertSecondsToSubtitleTime(0),
      endTime: convertSecondsToSubtitleTime(
        Number((nextStartSec - blockMargin).toFixed(3)),
      ),
    };
  }

  const currentEndTime = convertSubtitleTimeToSeconds(currentBlock.endTime);
  if (!nextBlock?.startTime) {
    // Append at the end a 2s block, shortened to
    // whatever time is left before the project duration limit.
    const nextStartTime = currentEndTime;
    const durationSec = getEndBlockDurationSec(nextStartTime, maxDurationSec);
    return {
      startTime: convertSecondsToSubtitleTime(Number(nextStartTime.toFixed(3))),
      endTime: convertSecondsToSubtitleTime(
        Number((nextStartTime + durationSec).toFixed(3)),
      ),
    };
  }

  const nextStartTime = convertSubtitleTimeToSeconds(nextBlock.startTime);

  return {
    startTime: convertSecondsToSubtitleTime(Number(currentEndTime.toFixed(3))),
    endTime: convertSecondsToSubtitleTime(
      Number((nextStartTime - blockMargin).toFixed(3)),
    ),
  };
};

/**
 * Format numeric string to SRT time format (HH:MM:SS,mmm)
 * Examples:
 * - "112233456" -> "11:22:33,456"
 * - "1122334567" -> "11:22:33,456" (truncate to 9 digits)
 * - "112233" -> "00:11:22,330" (pad with zeros)
 * - "11223345" -> "00:11:22,345" (pad with zeros)
 * @param {string} numericString - String containing only digits
 * @returns {string} - Formatted SRT time string (HH:MM:SS,mmm)
 */
const formatNumericToSRTTime = (numericString) => {
  // Remove all non-digit characters
  const digitsOnly = numericString.replace(/\D/g, '');

  if (!digitsOnly) return '';

  // Limit to 9 digits (HHMMSSmmm)
  const limitedDigits = digitsOnly.slice(0, 9);

  // Pad with zeros to ensure we have at least 9 digits for proper formatting
  const paddedDigits = limitedDigits.padEnd(9, '0');

  // Extract parts: HH, MM, SS, mmm
  const hours = paddedDigits.slice(0, 2);
  const minutes = paddedDigits.slice(2, 4);
  const seconds = paddedDigits.slice(4, 6);
  const milliseconds = paddedDigits.slice(6, 9);

  return `${hours}:${minutes}:${seconds},${milliseconds}`;
};

/**
 * Check if input is a numeric string that should be auto-formatted
 * Returns true if input is all digits and doesn't already match time format
 * Auto-format when:
 * - Input has 9+ digits (complete time format)
 * - Or input is being pasted (detected by digit count jump)
 * @param {string} inputValue - The input value to check
 * @param {number} previousDigitCount - Previous digit count for paste detection
 * @returns {boolean} - True if input should be auto-formatted
 */
const shouldAutoFormat = (inputValue, previousDigitCount = 0) => {
  // If empty or already in correct format, don't auto-format
  if (!inputValue) return false;

  // Check if it's already in time format (HH:MM:SS,mmm)
  if (DIGIT_SRT_TIME_REGEX.test(inputValue)) return false;

  // Check if it's all digits
  const allDigits = /^\d+$/.test(inputValue);
  if (!allDigits) return false;

  const currentDigitCount = inputValue.length; // Since inputValue is all digits, length = digit count

  // Auto-format if:
  // 1. Has 9+ digits (complete format)
  // 2. Or digit count jumped significantly (likely a paste operation)
  const isPasteOperation = currentDigitCount - previousDigitCount > 3;
  return currentDigitCount >= 9 || isPasteOperation;
};

/**
 * Auto-format time input as user types
 * Automatically inserts : and , at correct positions
 * Format: HH:MM:SS,mmm
 * Examples:
 * - "11" → "11:"
 * - "112" → "11:2"
 * - "1122" → "11:22:"
 * - "112233" → "11:22:33,"
 * - "1122334" → "11:22:33,4"
 * @param {string} inputValue - The input value to format
 * @returns {string} - Formatted time string with : and , inserted
 */
const autoFormatTimeInput = (inputValue) => {
  // Remove all non-digit characters to get only digits
  const digitsOnly = inputValue.replace(/\D/g, '');

  if (!digitsOnly) return '';

  // Limit to 9 digits (HHMMSSmmm)
  const limitedDigits = digitsOnly.slice(0, 9);

  let formatted = '';
  for (let i = 0; i < limitedDigits.length; i += 1) {
    formatted += limitedDigits[i];

    // After 2 digits (HH) → add :
    if (i === 1 && limitedDigits.length > 2) {
      formatted += ':';
    }
    // After 4 digits (HHMM) → add :
    else if (i === 3 && limitedDigits.length > 4) {
      formatted += ':';
    }
    // After 6 digits (HHMMSS) → add ,
    else if (i === 5 && limitedDigits.length > 6) {
      formatted += ',';
    }
  }

  return formatted;
};

const getAudioNameSrtAudio = (title, requestId) => {
  const titleWithoutSpecialCharacter = removeVietnameseTones(title).replace(
    /[^a-zA-Z0-9 ]/g,
    '',
  );

  let fileName = titleWithoutSpecialCharacter
    .trim()
    .replace(/\s+/g, '_')
    .toLowerCase();
  fileName = fileName || requestId;

  fileName = `${fileName}_${requestId}`;

  return fileName;
};

const formatInvalidTexts = (texts) => {
  if (!texts?.length) return '';
  return texts.map((text) => `"${text}"`).join(', ');
};

const calculateTranslateCredits = (texts = []) => {
  const totalChars = texts.reduce(
    (total, text) => total + countTextLength(text || ''),
    0,
  );
  return Math.ceil(totalChars * TRANSLATE_CREDIT_PER_CHAR);
};

/**
 * The two per-block operations that must never overlap.
 *
 * Both live on the block: `rewrite.status` is the AI rewrite, `status` is the
 * synthesis. The project-wide `isProjectSynthesizing` flag says nothing about
 * either — only the legacy whole-project SRT flow ever sets it — so a guard that
 * reads that flag lets the two run at once. Every guard reads the block.
 *
 * @param {object} block
 * @returns {boolean}
 */
const isBlockRewriting = (block) =>
  block?.rewrite?.status === REWRITE_STATUS.PENDING;

const isBlockSynthesizing = (block) =>
  block?.status === CONVERTING_STATUS.IN_PROGRESS;

/**
 * What the whole-project create-audio button should do right now.
 *
 * The rule: as long as ONE block still needs audio the button stays "Tạo audio"
 * and only fills in those blocks. Only when every block already has audio does
 * it become "Tạo lại audio", and then it re-synthesizes everything — which is
 * also why that variant asks for confirmation first, since it spends credits on
 * audio the user already owns.
 *
 * `blocks` is every block with text; `blocksNeedingAudio` the subset that has no
 * usable audio (missing, expired, or edited since it was made).
 *
 * `targetBlocks` excludes blocks already being synthesized, and blocks whose AI
 * rewrite is still pending — neither is submitted, so neither must be priced.
 * That makes it the one list behind the tooltip, the confirmation, and the run
 * itself, which is the only way those three can agree.
 *
 * A rewrite in flight is about to replace the block's text, so synthesizing the
 * old text spends credits on audio that is already stale.
 *
 * @param {{ blocks?: object[], blocksNeedingAudio?: object[] }} params
 * @returns {{ isReSynthesize: boolean, targetBlocks: object[] }}
 */
const resolveCreateAudioAction = ({
  blocks = [],
  blocksNeedingAudio = [],
} = {}) => {
  // The label is decided BEFORE dropping in-flight blocks: one that is being
  // synthesized for the first time still has no audio, so the button must keep
  // reading "Tạo audio" until it lands.
  const isReSynthesize = blocks.length > 0 && blocksNeedingAudio.length === 0;
  const candidates = isReSynthesize ? blocks : blocksNeedingAudio;
  return {
    isReSynthesize,
    targetBlocks: candidates.filter(
      (block) => !isBlockSynthesizing(block) && !isBlockRewriting(block),
    ),
  };
};

/**
 * The blocks a convert run will actually submit, out of a selection.
 *
 * Three rules, and they have to be applied in exactly one place: a block with no
 * text has nothing to synthesize, one already in flight is not submitted again,
 * and one whose AI rewrite is still pending would be synthesized from text that
 * is about to change. Splitting them across the toolbar and the container is
 * what let the button price one set and convert another — or drop every block
 * before the container ever saw the selection.
 *
 * With a translation live it is the translated text that gets synthesized, so
 * that is the field that decides whether a block counts as empty.
 *
 * @param {object[]} blocks
 * @param {{ useTranslatedText?: boolean }} options
 * @returns {object[]}
 */
const pickConvertibleBlocks = (blocks = [], { useTranslatedText } = {}) =>
  (blocks || []).filter(
    (block) =>
      (useTranslatedText ? block?.translatedText : block?.text)?.length &&
      !isBlockSynthesizing(block) &&
      !isBlockRewriting(block),
  );

/**
 * What a poll of the project's block statuses means for ONE block.
 *
 * The Backend is authoritative once it is talking about the synthesis we
 * actually submitted, and the audio URL is how that used to be decided: it
 * carries the request id, so a finished re-synthesis normally arrives with a new
 * link. It does NOT when the Backend answers with an existing request — same
 * text/voice/speed → same requestId → same audioLink — and then the link alone
 * can never tell the block finished, which left it spinning forever while every
 * poll returned SUCCESS.
 *
 * `submittedRequestId` is what closes that hole: it is the id the create call
 * handed back for THIS run (dropped again when the block is marked
 * IN_PROGRESS). A terminal status for that id is our result. A poll that lands
 * before the create response has no id to match, so it is still ignored — which
 * is what keeps a stale SUCCESS from the previous run out of the UI.
 *
 * @param {{ beBlock: object, localBlock?: object, submittedRequestId?: string }} params
 * @returns {{ status: string, audioExpiredAt: ?string, gotAudio: boolean }}
 *   `gotAudio` marks the poll that first publishes an audio, so the caller can
 *   realign the block's timeline slot exactly once.
 */
const resolvePolledBlockSynthesis = ({
  beBlock,
  localBlock,
  submittedRequestId,
} = {}) => {
  if (beBlock?.status === CONVERTING_STATUS.FAILURE) {
    return {
      status: CONVERTING_STATUS.FAILURE,
      audioExpiredAt: null,
      gotAudio: false,
    };
  }

  const isChangedAudioLink = beBlock?.audioLink
    ? beBlock.audioLink !== localBlock?.audioLink
    : false;
  const isOurRequest =
    Boolean(beBlock?.requestId) && beBlock.requestId === submittedRequestId;
  const hasFinishedOurRequest =
    isOurRequest &&
    localBlock?.status === CONVERTING_STATUS.IN_PROGRESS &&
    beBlock?.status === CONVERTING_STATUS.SUCCESS;
  const hasNewAudio = isChangedAudioLink || hasFinishedOurRequest;

  return {
    status:
      hasNewAudio || beBlock?.status === CONVERTING_STATUS.IN_PROGRESS
        ? beBlock?.status
        : localBlock?.status,
    audioExpiredAt: hasNewAudio
      ? beBlock?.audioExpiredAt
      : localBlock?.audioExpiredAt,
    gotAudio: hasNewAudio && beBlock?.status === CONVERTING_STATUS.SUCCESS,
  };
};

/**
 * The store update that takes blocks OUT of the synthesizing state after a
 * failed create-audio run.
 *
 * Every convert entry point drops blocks that are `IN_PROGRESS` (they must not
 * be submitted twice), so a block left in that state is unreachable: its
 * spinner never stops, the status poll never stops, and the whole-project
 * convert silently skips it. That makes settling a failure a hard requirement,
 * not a nicety — and it must be the SAME shape everywhere, which is why it
 * lives here: `status` drives the spinner, `blockStatus` drives the status
 * filter/chip, and they have to move together.
 *
 * @param {string[]} blockIds
 * @returns {Record<string, { status: string, blockStatus: string }>}
 */
const buildBlockSynthesisFailureUpdates = (blockIds = []) =>
  (blockIds || []).filter(Boolean).reduce((acc, blockId) => {
    acc[blockId] = {
      status: CONVERTING_STATUS.FAILURE,
      blockStatus: BLOCK_STATUS.ERROR,
    };
    return acc;
  }, {});

/**
 * Does the project have a block with no character at all?
 *
 * A block with no text has nothing to synthesize, so any audio/video export
 * built from the whole project would silently skip it — hence the download
 * gate. The "has text" test is deliberately the same as
 * pickConvertibleBlocks': `?.length`, no trim, and with a translation live it
 * is `translatedText` that decides — so a block is never rejected here while
 * still counting as convertible there.
 *
 * @param {Record<string, object>} blocks - redux `multiBlock.blocks`
 * @param {{ useTranslatedText?: boolean }} options
 * @returns {boolean}
 */
const hasEmptyTextBlock = (blocks, { useTranslatedText = false } = {}) =>
  Object.values(blocks || {}).some(
    (block) =>
      !(useTranslatedText ? block?.translatedText : block?.text)?.length,
  );

/**
 * Credits a set of blocks will consume when synthesized — the number shown in
 * the "Tiêu hao x điểm" tooltip and in the create-audio confirmation.
 *
 * The voice a block is charged for is its SPEAKER's voice, resolved through
 * `block.speakerId` — that is what the editor synthesizes with and what the
 * per-block counter already prices (Dubbing ContentBlock reads
 * `speaker?.voice?.creditFactor`). `block.voice` is only a cached copy, written
 * when the user picks a voice or speaker in this session and absent on a
 * freshly loaded project, so pricing off it quoted every block at the project
 * default factor right after opening a project — a x2 voice billed as x1.
 *
 * A block with no speaker of its own is charged the project default — which is
 * also the whole story when the speaker-config flag is off, since no speakers
 * are fetched then and every block prices off `voiceDefault`.
 *
 * With a successful translation active the synthesized text is the translated
 * one, so that is what gets counted.
 *
 * @param {object[]} blocks
 * @param {{ speakers?: object[], voiceDefault?: object, useTranslatedText?: boolean }} options
 * @returns {number}
 */
const calculateBlocksSynthesisCredits = (
  blocks = [],
  { speakers = [], voiceDefault, useTranslatedText = false } = {},
) =>
  (blocks || []).reduce((total, block) => {
    const speaker = (speakers || []).find(
      (item) => item?.id && item.id === block?.speakerId,
    );
    const voice = speaker?.voice || voiceDefault;
    const factor = voice?.creditFactor || 0;
    const text = useTranslatedText ? block?.translatedText : block?.text;
    return total + factor * countTextLength(text || '');
  }, 0);

// Credits a video export consumes, shown as "Tiêu hao x điểm" in the download
// menu. Display only — the authoritative charge is computed in
// aivoice-presentation.
// TODO: replace this fixed placeholder with the real pricing (ideally sourced
// from the backend) once the business flow is defined.
const calculateVideoExportCredits = () => 0;

const handleTranslateProject = async ({
  projectId,
  sourceLanguage,
  targetLanguage,
  blocks,
}) => {
  const webhookUrl = `${API_URL}/api/v1${RESOURCE.PROJECTS}/${projectId}${RESOURCE.TRANSLATE}/webhook`;

  const response = await apis.project.translateProject({
    projectId,
    sourceLanguage,
    targetLanguage,
    webhookUrl,
    blocks,
  });

  return response;
};

const isVideoSource = (source) => source?.type === PROJECT_TYPE.VIDEO;

/** Extraction never finished, so the project has no content to open. */
const isExtractionFailed = (project) =>
  project?.source?.extraction?.response?.status === EXTRACTION_STATUS.FAILURE;

/** Video and SRT are both DUBBING, so the kind comes off the source. Pure. */
const getProjectTypeKind = (project) => {
  if (project?.product === PROJECT_PRODUCT.PRESENTATION)
    return PROJECT_TYPE_KIND.SLIDE;
  if (project?.source?.type === PROJECT_TYPE.VIDEO)
    return PROJECT_TYPE_KIND.VIDEO;
  // `type`, not `source`: toCardProject always builds the object below.
  if (project?.product === PROJECT_PRODUCT.DUBBING && !project?.source?.type)
    return PROJECT_TYPE_KIND.SRT;
  return PROJECT_TYPE_KIND.AUDIO;
};

/**
 * The part of a project that a recent-projects card or row actually reads.
 *
 * The list endpoint offers no field selection — `fields` comes back from request
 * validation as "not allowed", and so does every other name for it — so the whole
 * project arrives whatever the client does. What it does not have to do is STAY.
 * Measured on one page of ten, `blocks` was 534KB of a 542KB response (one
 * project carried 879 of them, and their `audioLink`s alone were 141KB), while
 * all a card wants out of that array is a first line of text and a slide's
 * picture. The grid appends page after page and never drops one, so holding the
 * raw objects means carrying half a megabyte per ten projects scrolled past, for
 * the life of the page. `musicBlocks` goes the same way — nothing on that screen
 * has ever read it.
 *
 * Derived rather than kept, so the arrays are collectable with the response:
 *   - `firstText` — leading blocks are often silence or a pause with nothing to
 *     show, so this is the first block that actually has words.
 *   - `previewImageUrl` — the rendered page a PRESENTATION shows.
 *   - `failed` — extraction never finished, so there is nothing to open.
 *
 * `source` keeps its shape, trimmed to what is read, so callers can still hand it
 * to `isVideoSource`. Pure.
 */
const toCardProject = (project) => {
  const blocks = project.blocks || [];
  return {
    id: project.id,
    title: project.title,
    product: project.product,
    updatedAt: project.updatedAt,
    source: { type: project.source?.type, url: project.source?.url },
    firstText: blocks.find((block) => block.text?.trim())?.text.trim() || '',
    previewImageUrl:
      blocks.find((block) => block.previewImageUrl)?.previewImageUrl || '',
    failed: isExtractionFailed(project),
  };
};

const getSourceUrl = (source) => source?.url || '';

/**
 * Should the transcript-extraction loading be scoped to the block list instead
 * of blurring the whole editor?
 *
 * Only in the new video-dubbing editor: there the video preview and the
 * timeline are meaningful while the transcript is still being extracted, so
 * covering them hides context for no reason. Every other project (SRT/audio
 * dubbing, or the flag off) keeps the full-screen overlay.
 *
 * @param {{ source?: object, timelineTracksEnabled?: boolean }} params
 * @returns {boolean}
 */
const isBlockScopedLoading = ({ source, timelineTracksEnabled } = {}) =>
  Boolean(timelineTracksEnabled) && isVideoSource(source);

const parseStoredAspectRatio = (value) => {
  if (!value) {
    return {
      aspectRatio: ASPECT_RATIO_VALUES.ORIGINAL,
    };
  }

  if (Object.values(ASPECT_RATIO_VALUES).includes(value)) {
    return {
      aspectRatio: value,
    };
  }

  return {
    aspectRatio: ASPECT_RATIO_VALUES.ORIGINAL,
  };
};

const isProjectCaptionStyleId = (styleId) =>
  /^[0-9a-f]{24}$/i.test(styleId ?? '');

// The project's own style: the id the API gave it, or the marker it carries
// until a save answers with one.
const isOwnCaptionStyle = (styleId) =>
  styleId === CAPTION_STYLE_CUSTOM || isProjectCaptionStyleId(styleId);

const withCustomCaptionStyle = (videoSettings) => {
  const captionStyle = videoSettings?.captionStyle;
  if (!captionStyle?.config || !isOwnCaptionStyle(captionStyle.styleId))
    return videoSettings;
  return { ...videoSettings, customCaptionStyle: captionStyle };
};

const normalizeProjectCaptionStyle = (captionTemplate) => {
  const { enabled, projectTemplateId, templateCode, config } =
    captionTemplate ?? {};
  const styleId = enabled && (projectTemplateId || templateCode);
  if (!styleId) return { styleId: CAPTION_STYLE_NONE };
  return config ? { styleId, config } : { styleId };
};

// The API answers with the project's own style whether or not it is the one in
// use, so its card survives a reload.
const toCustomCaptionStyle = (projectCaptionTemplate) => {
  const { id, config } = projectCaptionTemplate ?? {};
  return id && config ? { styleId: id, config } : undefined;
};

const normalizeProjectVideoSettings = (
  videoSettings,
  captionTemplate,
  projectCaptionTemplate,
) => {
  const parsedAspectRatio = parseStoredAspectRatio(videoSettings?.aspectRatio);
  return withCustomCaptionStyle({
    ...parsedAspectRatio,
    backgroundColor:
      videoSettings?.backgroundColor || DEFAULT_VIDEO_BACKGROUND_COLOR,
    captionStyle: normalizeProjectCaptionStyle(captionTemplate),
    customCaptionStyle: toCustomCaptionStyle(projectCaptionTemplate),
  });
};

// The fields of a caption style the API accepts, by group. Derived from the
// shared defaults so the list cannot drift; fontFamily and blockWidth carry no
// default but the API requires them.
const CAPTION_CONFIG_FIELDS = Object.fromEntries(
  Object.entries(CAPTION_STYLE_DEFAULTS).map(([group, fields]) => [
    group,
    [
      ...Object.keys(fields),
      ...(group === 'text' ? ['fontFamily'] : []),
      ...(group === 'layout' ? ['blockWidth'] : []),
    ],
  ]),
);

const pickFields = (source, fields) =>
  Object.fromEntries(
    fields
      .filter((field) => source?.[field] !== undefined)
      .map((field) => [field, source[field]]),
  );

// A whitelist, not a blacklist: the read path enriches what it returns (today
// text.font) and the write path rejects anything it does not own.
const buildCaptionConfigPayload = (config) =>
  Object.fromEntries(
    Object.entries(CAPTION_CONFIG_FIELDS).map(([group, fields]) => [
      group,
      pickFields(config?.[group], fields),
    ]),
  );

const buildCaptionTemplatePayload = (captionStyle) => {
  const styleId = captionStyle?.styleId;
  if (!styleId) return undefined;
  if (styleId === CAPTION_STYLE_NONE) return { enabled: false };
  // Still unnamed: the config save answers with the id, and the assignment is
  // built again from it.
  if (styleId === CAPTION_STYLE_CUSTOM) return undefined;
  return isProjectCaptionStyleId(styleId)
    ? { enabled: true, projectTemplateId: styleId }
    : { enabled: true, templateCode: styleId };
};

const serializeAspectRatio = (aspectRatio) => {
  if (!aspectRatio) {
    return undefined;
  }

  // 'original' is persisted as-is (Backend + render chain accept it since
  // video-renderer 0.3.10): stripping it made "Tỷ lệ gốc" unrepresentable —
  // the Backend could not tell "user chose original" from "never chose" and
  // fell back to 16:9 at export.
  return aspectRatio;
};

const buildVideoSettingsPayload = ({
  aspectRatioEnabled,
  backgroundColorEnabled,
  videoSettings,
}) => {
  const payload = {};

  if (aspectRatioEnabled) {
    const serializedAspectRatio = serializeAspectRatio(
      videoSettings?.aspectRatio,
    );
    if (serializedAspectRatio) {
      payload.aspectRatio = serializedAspectRatio;
    }
  }

  if (backgroundColorEnabled && videoSettings?.backgroundColor) {
    payload.backgroundColor = videoSettings.backgroundColor;
  }

  return Object.keys(payload).length ? payload : undefined;
};

const buildSampleField = (block, autoSaveEnabled) => {
  if (!autoSaveEnabled) return { isSample: block?.isSample || false };
  return block?.isSample === true ? { isSample: true } : {};
};

// Serialize one store block into the Backend block-create shape (id + content).
// Shared by full-project save and auto-save ADD so the contract can't drift.
const buildBlockCreatePayload = (
  block,
  blockId,
  { voiceDefault, isPresentation = false, autoSaveEnabled = false } = {},
) => ({
  id: blockId,
  title: block.title || undefined,
  characters: countTextLength(block.text || ''),
  text: block.text || '',
  ...(isPresentation && {
    currentNote: normalizeNote(block.currentNote),
  }),
  ...(block?.translatedText != null && {
    translatedText: block.translatedText,
  }),
  voiceCode: block?.voiceCode || voiceDefault?.code,
  breakTime: block?.breakTime || DEFAULT_BREAK_TIME,
  speed: block?.speed || DEFAULT_SPEED,
  elements: block?.elements,
  ...buildSampleField(block, autoSaveEnabled),
  startTime: block?.startTime || undefined,
  speakerId: block?.speakerId || undefined,
  endTime: block?.endTime || undefined,
  ...(block?.hasEmphasis ? { emphasisIntensity: block?.emphasis } : {}),
});

const buildProjectPayload = ({
  title,
  blockIds,
  blocks,
  voiceDefault,
  audioType,
  videoSettings,
  isPresentation = false,
}) => ({
  title,
  blocks: blockIds.map((blockId) =>
    buildBlockCreatePayload(blocks[blockId], blockId, {
      voiceDefault,
      isPresentation,
    }),
  ),
  audioType,
  ...(videoSettings ? { videoSettings } : {}),
});

const isVideoSettingsChanged = (currentVideoSettings, originalVideoSettings) =>
  JSON.stringify(currentVideoSettings) !==
  JSON.stringify(originalVideoSettings);

const isNoteChanged = (block) =>
  normalizeNote(block?.originalNote) !== normalizeNote(block?.currentNote);

const hasNarrationConfig = (source) => {
  const config = source?.extraction?.config;
  const generateConfig = source?.generation?.config;
  if (config == null && generateConfig == null) return false;
  if (typeof config === 'object') return Object.keys(config).length > 0;
  if (typeof generateConfig === 'object')
    return Object.keys(generateConfig).length > 0;
  return Boolean(config) || Boolean(generateConfig);
};

// Whether a block's text area should still render the skeleton placeholder
// while the source is being extracted (PENDING). A block stops being a text
// skeleton as soon as its real text arrives, even if extraction is ongoing.
// - AI narration: text is genuine once it differs from the original note.
// - Otherwise: only skeleton while no block has any text yet (empty text is
//   genuine once some block already received text).
const isBlockTextSkeleton = ({
  block,
  isSkeletonUI,
  isAINarration,
  allTextsEmpty,
}) => {
  if (!isSkeletonUI) return false;
  const text = block?.text || '';
  if (isAINarration) return !text || text === (block?.originalNote || '');
  return !text && allTextsEmpty;
};

// During extraction (PENDING) a slide block is considered fully loaded once its
// text has streamed in (text skeleton resolved — empty text/note is valid) AND
// its thumbnail (previewImageUrl) has arrived. Mirrors the per-block skeleton
// decisions in ContentBlock so the toolbar progress stays in sync with the list.
const isBlockExtracted = ({ block, isAINarration, allTextsEmpty }) =>
  Boolean(block?.previewImageUrl) &&
  !isBlockTextSkeleton({
    block,
    isSkeletonUI: true,
    isAINarration,
    allTextsEmpty,
  });

// Counts how many slide blocks have finished extracting vs the total, used to
// render the "Hệ thống đang xử lý... (done/total slide)" toolbar progress.
const countExtractedBlocks = (blocks = {}, source = null) => {
  const arr = Object.values(blocks);
  const total = arr.length;
  if (!total) return { done: 0, total: 0 };

  const isAINarration = hasNarrationConfig(source);
  const allTextsEmpty = arr.every((block) => !block?.text);
  const done = arr.filter((block) =>
    isBlockExtracted({ block, isAINarration, allTextsEmpty }),
  ).length;

  return { done, total };
};

const resolveAiOptionsTipText = ({ isAudio, isPptx, slideV2Enabled, t }) => {
  if (isAudio) return t('transcriptLanguageTip');
  if (isPptx && slideV2Enabled) return t('aiPresentationTimeTip');
  return null;
};

const isExportPending = (entry) => entry?.status === EXPORT_STATUS.PENDING;

// Presentation: derive active export formats from per-type status.
// Order is fixed (video, slide, then audio) for stable banner text.
const getPresentationExportFormats = (exports) => {
  const formats = [];
  if (isExportPending(exports?.[EXPORT_TYPE.VIDEO])) formats.push('video');
  if (isExportPending(exports?.[EXPORT_TYPE.PPTX])) formats.push('slide');
  if (isExportPending(exports?.[EXPORT_TYPE.SEPARATE_AUDIO]))
    formats.push('audio');
  return formats;
};

// Maps Redux export/download state to banner state.
// Presentation exports slide/video via per-type `exports` AND audio either
// asynchronously (per-block zip on v2) or client-side via the generic
// `isDownloading` flag (merged MP3, and the per-block zip on v1); TTS/Dubbing
// only download audio via `isDownloading`.
const getExportingState = ({ exports, isDownloading, product }) => {
  if (product === PROJECT_PRODUCT.PRESENTATION) {
    const formats = getPresentationExportFormats(exports);
    if (isDownloading) formats.push('audio');
    return { isExporting: formats.length > 0, formats };
  }

  // TTS/Dubbing download audio via `isDownloading`; video dubbing additionally
  // exports audio asynchronously (merged JOIN_AUDIO, per-block SEPARATE_AUDIO
  // zip, or selected-blocks SEPARATE_AUDIO_PARTIAL zip) and renders an mp4
  // (VIDEO), each tracked as a pending export. Audio and video can be in flight
  // at the same time, so both formats are reported.
  const audioExporting =
    isDownloading ||
    isExportPending(exports?.[EXPORT_TYPE.JOIN_AUDIO]) ||
    isExportPending(exports?.[EXPORT_TYPE.SEPARATE_AUDIO]) ||
    isExportPending(exports?.[EXPORT_TYPE.SEPARATE_AUDIO_PARTIAL]);
  const formats = [];
  if (audioExporting) formats.push('audio');
  if (isExportPending(exports?.[EXPORT_TYPE.VIDEO])) formats.push('video');
  return { isExporting: formats.length > 0, formats };
};

// The mp4 render is the only export whose output depends on the video settings
// (aspect ratio, background colour), so it is the only one that has to freeze
// them — an audio export doesn't care.
const isVideoExportPending = (exports) =>
  isExportPending(exports?.[EXPORT_TYPE.VIDEO]);

const formatExportLabel = (formats) => formats.join(', ');

/**
 * Display state of one row in the export history.
 *
 * Retention is read from the record, never from the user as they are today:
 * `retentionPeriod` is the span the package the file was exported under allows,
 * and `completedAt` is the moment the backend counts that span from. A downgrade
 * must not shorten a file already exported, and an upgrade must not revive an
 * expired one.
 *
 * `createdAt` and `fallbackRetentionPeriod` answer only for a record carrying
 * neither field. `createdAt` timestamps the request rather than the finished
 * file, so it lands earlier by however long the export waited in the queue.
 *
 * Either way the span is counted in whole **calendar** days: a file finished on
 * Sunday at 15:20 stops being downloadable at 00:00 on Wednesday, not at 15:20
 * — hence `startOf('day')` before the period is added.
 */
const getExportItemState = (
  item,
  fallbackRetentionPeriod = EXPORT_RETENTION_PERIOD,
) => {
  const isPending = item?.status === EXPORT_STATUS.PENDING;
  const isFailed = item?.status === EXPORT_STATUS.FAILURE;
  const isSuccess = item?.status === EXPORT_STATUS.SUCCESS;

  const retentionDays = item?.retentionPeriod || fallbackRetentionPeriod;
  const countFrom = item?.completedAt || item?.createdAt;
  const expiredAt = countFrom
    ? moment(countFrom).startOf('day').add(retentionDays, 'days')
    : null;
  const isExpired = expiredAt ? moment().isAfter(expiredAt) : false;

  return {
    isPending,
    isFailed,
    isExpired: isSuccess && isExpired,
    canDownload: isSuccess && !isExpired && !!item?.fileUrl,
    retentionDays,
  };
};

/**
 * Append an older page of export records to the list already on screen.
 *
 * Records already present are dropped. The cursor keeps the pages apart on its
 * own, so this is a guard rather than the mechanism: a record that also arrived
 * through a polling refresh, or a page boundary the backend repeats, must not
 * show up twice. The order within each page comes from the backend and is left
 * untouched.
 */
const appendExportPage = (items = [], olderPage = []) => {
  const loadedIds = new Set(items.map((item) => item?.id));
  return [...items, ...olderPage.filter((item) => !loadedIds.has(item?.id))];
};

/**
 * Ids of the export records still running, from both places one can be known.
 *
 * `exports[type]` knows an export started in this session before any history
 * page was loaded; `items` knows one started elsewhere and read back from the
 * list. Only the types the backend keeps records for are asked about — the
 * block-toolbar export has no record to name.
 */
const collectPendingExportIds = ({ exports, items }) => {
  const fromTypes = Object.entries(exports || {})
    .filter(
      ([type, entry]) =>
        EXPORT_HISTORY_TYPES.includes(type) &&
        entry?.status === EXPORT_STATUS.PENDING &&
        entry?.requestId,
    )
    .map(([, entry]) => entry.requestId);
  const fromItems = (items || [])
    .filter((item) => item?.status === EXPORT_STATUS.PENDING && item?.id)
    .map((item) => item.id);

  return [...new Set([...fromTypes, ...fromItems])];
};

/**
 * Fields of an export record that belong in `state.project.exports[type]`.
 *
 * The record is the whole row; the per-type entry holds only what the export
 * flow reads, plus `blockIds`, which the record does not carry and which the
 * caller keeps by spreading the previous entry.
 */
const toExportEntry = (record) => ({
  status: record?.status,
  fileUrl: record?.fileUrl,
  fileName: record?.fileName,
  requestId: record?.id,
});

/**
 * Apply progress answers to the records already loaded and to the per-type
 * entries pointing at them.
 *
 * Both halves move together so no reader can observe a record and its per-type
 * entry disagreeing. A record is matched by `id`, a per-type entry by its
 * `requestId`: one type can have several records, and only the one the entry
 * points at may write to it. Nothing is added and nothing is removed — the list
 * grows only by loading a page.
 */
const mergeProgressIntoState = ({ items, exports }, records) => {
  const byId = new Map(
    (records || []).filter((record) => record?.id).map((r) => [r.id, r]),
  );
  if (!byId.size) return { items: items || [], exports };

  const nextItems = (items || []).map((item) =>
    byId.has(item?.id) ? { ...item, ...byId.get(item.id) } : item,
  );
  const nextExports = exports
    ? Object.entries(exports).reduce(
        (acc, [type, entry]) => ({
          ...acc,
          [type]: byId.has(entry?.requestId)
            ? { ...entry, ...toExportEntry(byId.get(entry.requestId)) }
            : entry,
        }),
        {},
      )
    : exports;

  return { items: nextItems, exports: nextExports };
};

/**
 * Translation key and interpolation for a history row's file-type label.
 *
 * JOIN_AUDIO needs to say mp3 or wav. The extension is read off `fileName`
 * rather than the project's current `audioType`, because the user can change
 * that setting after exporting while the exported file keeps its format. A
 * PENDING record has no file name yet, so it falls back to `audioType`.
 */
const getExportTypeLabel = (item, fallbackAudioType) => {
  const labels = {
    [EXPORT_TYPE.VIDEO]: { key: 'videoFileType' },
    [EXPORT_TYPE.PPTX]: { key: 'pptxFileType' },
    [EXPORT_TYPE.SEPARATE_AUDIO]: { key: 'audioZipFileType' },
    [EXPORT_TYPE.SRT]: { key: 'srtFile' },
  };

  // One merged audio file, whichever flow produced it: JOIN_AUDIO from per-block
  // audio, AUDIO_SRT from the whole-project synthesis. The reader is told the
  // format, which only the file name carries.
  const isMergedAudio =
    item?.type === EXPORT_TYPE.JOIN_AUDIO ||
    item?.type === EXPORT_TYPE.AUDIO_SRT;

  if (isMergedAudio) {
    const extension = item?.fileName?.split('.').pop();
    const format =
      extension && extension !== item.fileName ? extension : fallbackAudioType;
    return { key: 'audioFile', params: { format: format?.toLowerCase() } };
  }

  return labels[item?.type] || null;
};

/**
 * Convert transcribe-chunk `extractionProgress.batches` into a Redux-ready
 * { blocks, blockIds } structure, sorted by batch number then position.
 * Temp IDs ("{batchNum}-{idx}") are replaced by real IDs on extraction SUCCESS.
 *
 * @param {Object} extractionProgress - { batches: { [batchNum]: [{text, startTime, endTime}] } }
 * @returns {{ blocks: Object, blockIds: string[] }}
 */
const convertExtractionProgressToBlocks = (extractionProgress) => {
  const batches = extractionProgress?.batches || {};
  const sortedBatchNums = Object.keys(batches)
    .map(Number)
    .sort((a, b) => a - b);

  const flattenedSegments = sortedBatchNums.flatMap((batchNum) =>
    (batches[String(batchNum)] || []).map((item, idx) => ({
      batchNum,
      item,
      idx,
    })),
  );

  return flattenedSegments.reduce(
    (acc, { batchNum, item, idx }) => {
      const id = `${batchNum}-${idx + 1}`;
      return {
        blocks: {
          ...acc.blocks,
          [id]: {
            id,
            text: item.text || '',
            startTime: convertSecondsToSubtitleTime(item.startTime ?? 0),
            endTime: convertSecondsToSubtitleTime(item.endTime ?? 0),
            speed: 1,
          },
        },
        blockIds: [...acc.blockIds, id],
      };
    },
    { blocks: {}, blockIds: [] },
  );
};

// Block timestamp slot duration in seconds, or null when the timestamps are
// missing / invalid / non-positive.
const getBlockTimestampDuration = ({ startTime, endTime } = {}) => {
  if (!startTime || !endTime) return null;
  const start = convertSubtitleTimeToSeconds(startTime);
  const end = convertSubtitleTimeToSeconds(endTime);
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  const duration = end - start;
  return duration > 0 ? duration : null;
};

// Effective (trimmed) audio playback duration in seconds:
//   MIN(block timestamp duration, audio duration).
// Falls back to the raw audio duration when the block timestamp is unavailable,
// and to 0 when the audio duration itself is unknown/invalid.
const getTrimmedAudioDuration = ({
  startTime,
  endTime,
  audioDuration,
} = {}) => {
  if (!audioDuration || Number.isNaN(audioDuration) || audioDuration <= 0) {
    return 0;
  }
  const blockDuration = getBlockTimestampDuration({ startTime, endTime });
  if (!blockDuration) return audioDuration;
  return Math.min(blockDuration, audioDuration);
};

// Translating (adds translatedText) or deleting the translation (reverts to the
// original text) makes any previously synthesized block audio no longer match the
// text shown to the user. Reset the audio-related fields so the block falls back
// to the "not synthesized yet" state (Play button → Convert button), mirroring
// how SAVE_PROJECT / pronunciation changes invalidate a block's audio on edit.
const invalidateBlockAudio = (block) => {
  if (!block) return block;
  return {
    ...block,
    audioLink: null,
    status: null,
    requestId: null,
    audioExpiredAt: null,
  };
};
// Backing store for a canvas of the given CSS box: css x dpr, capped so the
// longest edge stays under MAX_CANVAS_DIMENSION.
const computeCanvasPixelSize = (cssWidth, cssHeight, devicePixelRatio) => {
  if (!cssWidth || !cssHeight) return { width: 0, height: 0 };
  const dpr = Math.max(1, devicePixelRatio || 1);
  const scale = Math.min(
    dpr,
    MAX_CANVAS_DIMENSION / cssWidth,
    MAX_CANVAS_DIMENSION / cssHeight,
  );
  return {
    width: Math.round(cssWidth * scale),
    height: Math.round(cssHeight * scale),
  };
};

export {
  computeCanvasPixelSize,
  getExportingState,
  getExportItemState,
  getExportTypeLabel,
  appendExportPage,
  mergeProgressIntoState,
  collectPendingExportIds,
  toExportEntry,
  isVideoExportPending,
  formatExportLabel,
  buildBlockCreatePayload,
  convertBlocksSaveToStore,
  countTextLengthBlocks,
  checkBlocksChanged,
  isBlockChanged,
  audioExpired,
  getBlockChanged,
  getBlockWithAudio,
  getBlockStatus,
  resolveBlockVoice,
  getVoiceFromBlockSelected,
  getSpeakerFromBlockSelected,
  getSpeedFromBlockSelected,
  handleDownloadParagraphsZip,
  getEmphasisFromBlockSelected,
  assignEmphasisToBlocks,
  sanitizeTitleFile,
  validateBlockTimeChange,
  getAudioAdjustedBlockEndTime,
  buildInsertedBlockTiming,
  formatNumericToSRTTime,
  shouldAutoFormat,
  autoFormatTimeInput,
  getAudioNameSrtAudio,
  getDubbingBlockChanged,
  getVideoDubbingBlockChanged,
  formatInvalidTexts,
  checkBlockTextError,
  calculateTranslateCredits,
  calculateBlocksSynthesisCredits,
  resolveCreateAudioAction,
  getEffectiveText,
  pickConvertibleBlocks,
  isBlockRewriting,
  isBlockSynthesizing,
  buildBlockSynthesisFailureUpdates,
  resolvePolledBlockSynthesis,
  hasEmptyTextBlock,
  calculateVideoExportCredits,
  handleTranslateProject,
  getFileIcon,
  isVideoSource,
  isExtractionFailed,
  getProjectTypeKind,
  toCardProject,
  getSourceUrl,
  isBlockScopedLoading,
  parseStoredAspectRatio,
  normalizeProjectVideoSettings,
  isOwnCaptionStyle,
  isProjectCaptionStyleId,
  withCustomCaptionStyle,
  buildCaptionTemplatePayload,
  buildCaptionConfigPayload,
  serializeAspectRatio,
  buildVideoSettingsPayload,
  buildProjectPayload,
  isVideoSettingsChanged,
  normalizeNote,
  isNoteChanged,
  hasNarrationConfig,
  isBlockTextSkeleton,
  countExtractedBlocks,
  resolveAiOptionsTipText,
  convertExtractionProgressToBlocks,
  getBlockTimestampDuration,
  getTrimmedAudioDuration,
  invalidateBlockAudio,
};
